import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  Button,
  OutlinedInput,
  Autocomplete,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import PatientSearchDialog from "../searchPatientDialog";
import axios from "axios";
import { Cross, CrossIcon, Save } from "lucide-react";
import { Add, Cancel, CloudUpload, FamilyRestroomRounded, Visibility } from "@mui/icons-material";
import InvoiceThermalReceipt from "../../../components/InvoiceThermalReceipt";
import { useSelector } from "react-redux";
import AccountPatientRegistration from "../patientRegistration";
import AttachmentViewer from "../../../components/AttachmentViewer";

interface PatientData {
  _id: string;
  mrn?: string | number;
  patientName: string;
  guardiansName: string;
  gender: string;
  dob: string;
  phonNumber: string;
  city: string;
}

interface Doctor {
  _id: string;
  fullName: string;
}

interface DoctorShare {
  doctorId: string;
  share: number;
  shareMode: string;
  amount: number;
  discount: number;
  netAmount: number;
  doctorName: string;
  originalShare?: number;
}

interface IncomeCategory {
  _id: string;
  categoryName: string;
  description?: string;
  price: number;
  discount: number;
  doctors: any[];
  discountMode: string;
  doctorShare: DoctorShare[];
}

interface ProcedureData {
  categoryId: string;
  categoryName: string;
  description: string;
  price: number;
  quantity: number;
  total: number;
  discount: number;
  discountMode: string;
  doctorShares: DoctorShare[];
}

interface RegisterFormProps {
  onNext: () => void;
  setSelectedId: any;
  formData: PatientData;
  getId: (args: string) => void;
  storeData: any;
  selectedId: any;
}

const IncomeManagement: React.FC<RegisterFormProps> = () => {
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState("MRN");
  const [searchValue, setSearchValue] = useState("");
  const [openSearchList, setOpenSearchList] = useState(false);
  const [searchResults, setSearchResults] = useState<PatientData[]>([]);
  const location = useLocation();
  const { patient, editData } = location.state || {};
  const [incomeData, setIncomeData] = useState<IncomeCategory[]>([]);
  const [payment, setPayment] = useState<number>(editData?.payment || 0);
  const [advance, setAdvance] = useState<number>(editData?.advance || 0);
  const navigate = useNavigate();
  const projectProfile = useSelector(
    (state: any) => state.projectProfileSlice.projectProfile
  );
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [patientReg, setPatientReg] = useState(false);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    index: -1,
    value: null as IncomeCategory | null,
  });
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(
    editData?.doctorId
      ? {
        _id: editData.doctorId,
        fullName: editData.doctorName || "Unknown Doctor",
      }
      : null
  );
  const [invoiceNote, setInvoiceNote] = useState<string>(editData?.invoiceNote || "");
  const [file, setFile] = useState<File | null>(null);
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState<string>(editData?.attachmentUrl || "");
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      
      if (selectedFile.size > maxSize) {
        toast.error("File size must not exceed 5MB");
        e.target.value = ""; // Reset the input
        return;
      }
      
      setFile(selectedFile);
    }
  };
  const [procedures, setProcedures] = useState<ProcedureData[]>(
    editData?.procedureItems
      ? editData.procedureItems.map((item: any) => ({
        categoryId: item.incomeCategoryId || "",
        categoryName: item.categoryName || "",
        description: item.description || "",
        price: item.rate || 0,
        quantity: item.qty || 1,
        total: item.amount || 0,
        discount: item.discount || 0,
        discountMode: "value",
        doctorShares: item.shares
          ? item.shares.map((share: any) => ({
            doctorId: share.doctorId || "",
            share: share.share || 0,
            shareMode: share.shareMode || "%",
            amount: share.amount || 0,
            discount: share.discount || 0,
            netAmount: share.netAmount || share.amount || 0,
            doctorName: share.name || "Unknown Doctor",
          }))
          : [],
      }))
      : []
  );
  const handleOpen = () => setPatientReg(true);
  const handleClose = () => setPatientReg(false);

  const confirmProcedureChange = (
    index: number,
    value: IncomeCategory | null
  ) => {
    setProcedures(prev => {
      const newProcedures = [...prev];
      if (value) {
        const newPrice = value.price;
        const newTotal = newPrice * prev[index].quantity;
        const newDoctorShares = value.doctorShare.map(share => {
          const doctor = value.doctors.find(doc => doc._id === share.doctorId);
          const calculatedAmount =
            share.shareMode === "%"
              ? (newTotal * share.share) / 100
              : share.share;
          return {
            ...share,
            amount: calculatedAmount,
            discount: 0,
            netAmount: calculatedAmount,
            doctorName: doctor ? doctor.fullName : "Unknown Doctor",
            originalShare: share.share, // Store the original share value
          };
        });

        // Calculate default discount amount from category
        const defaultDiscountAmount =
          value.discountMode === "%"
            ? (newTotal * (value.discount || 0)) / 100
            : value.discount || 0;

        newProcedures[index] = {
          ...prev[index],
          categoryId: value._id,
          categoryName: value.categoryName,
          description: value.description || "",
          price: newPrice,
          total: newTotal,
          discount: Math.round(defaultDiscountAmount),
          discountMode: "value",
          doctorShares: newDoctorShares,
        };
      } else {
        newProcedures[index] = {
          ...prev[index],
          categoryId: "",
          categoryName: "",
          description: "",
          price: 0,
          total: 0,
          discount: 0,
          discountMode: "value",
          doctorShares: [],
        };
      }
      return newProcedures;
    });
  };

  const [selectedPatient, setSelectedPatient] = useState<PatientData | null>({
    _id: editData?.patientId || patient?._id || "",
    mrn: editData?.patientInfo?.mrn || patient?.mrn || "",
    patientName:
      editData?.patientInfo?.patientName || patient?.patientName || "",
    guardiansName:
      editData?.patientInfo?.guardiansName || patient?.guardiansName || "",
    gender: editData?.patientInfo?.gender || patient?.gender || "",
    dob: editData?.patientInfo?.dob || patient?.dob || "",
    phonNumber: editData?.patientInfo?.phonNumber || patient?.phonNumber || "",
    city: editData?.patientInfo?.city || patient?.city || "",
  });

  const handleSearchTypeChange = (event: any) => {
    setSearchType(event.target.value);
    setSearchValue("");
  };

  const handleSearch = () => {
    setOpenSearchList(true);
    setLoading(true);
    let endpoint = "";
    switch (searchType) {
      case "Name":
        endpoint = `searchBy=name&search=${searchValue}`;
        break;
      case "CNIC":
        endpoint = `searchBy=cnic&search=${searchValue}`;
        break;
      case "MRN":
        endpoint = `searchBy=mrn&search=${searchValue}`;
        break;
      case "Phone Number":
        endpoint = `searchBy=mobileNo&search=${searchValue}`;
        break;
      default:
        console.error("Invalid search type");
        setLoading(false);
        return;
    }

    fetch(
      `${process.env.REACT_APP_ADMIN_API_URL}/patient-registration/searchPatient?${endpoint}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error("Network response was not ok");
        }
      })
      .then(data => {
        const patients = data?.data;
        setSearchResults(patients || []);
        if (searchType === "MRN" && patients && !Array.isArray(patients)) {
          toast.success("Record Found");
        } else if (searchType === "MRN" && !patients) {
          toast.error("Record not Found");
        }
      })
      .catch(error => {
        console.error("Error fetching data:", error);
        toast.error("Search failed");
      })
      .finally(() => setLoading(false));
  };

  const fetchDataIncome = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income-category/getAllCategories`,
        {
          params: { search: searchValue },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setIncomeData(response.data?.data || []);
      // setDoctors(response.data?.doctors || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getAllSharerDoctors`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDoctors(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching doctors:", error);
      toast.error("Error fetching doctors");
    }
  };

  useEffect(() => {
    fetchDataIncome();
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (incomeData.length > 0 && procedures[0]?.categoryId === "") {
      const defaultProcedure = incomeData[0];
      handleProcedureChange(0, null, defaultProcedure);
    }
  }, [incomeData]);

  useEffect(() => {
    const { grandTotal } = computeSummary();
    // Set payment to grandTotal by default when procedures change, unless it's an edit with existing payment
    if (!editData) {
      setPayment(grandTotal);
    }
  }, [procedures]);

  useEffect(() => {
    const { grandTotal } = computeSummary();
    setAdvance(Math.max(0, payment - grandTotal));
  }, [procedures, payment]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    if (patient) {
      setProcedures(prev => [
        {
          categoryId: "",
          categoryName: "",
          description: "",
          price: 0,
          quantity: 1,
          total: 0,
          discount: 0,
          discountMode: "value",
          doctorShares: [],
        },
      ]);
      !editData && setSelectedPatient(patient);
    }
  }, [patient]);

  const addProcedureSection = () => {
    setProcedures(prev => [
      ...prev,
      {
        categoryId: "",
        categoryName: "",
        description: "",
        price: 0,
        quantity: 1,
        total: 0,
        discount: 0,
        discountMode: "value",
        doctorShares: [],
      },
    ]);
  };

  const removeProcedureSection = (index: number) => {
    setProcedures(prev => prev.filter((_, i) => i !== index));
  };

  const handleProcedureChange = (
    index: number,
    event: React.SyntheticEvent | null,
    value: IncomeCategory | null
  ) => {
    if (
      value &&
      procedures.some(
        p => p.categoryId === value._id && procedures.indexOf(p) !== index
      )
    ) {
      setConfirmDialog({ open: true, index, value });
    } else {
      confirmProcedureChange(index, value);
    }
  };

  const handleQuantityChangeAt = (index: number, value: string) => {
    setProcedures(prev => {
      const qty = Math.max(1, Number.parseInt(value) || 1);
      const p = prev[index];
      const total = p.price * qty;
      const doctorShares = p.doctorShares.map(share => {
        if (share.shareMode === "%") {
          const amount = (share.share / 100) * total;
          const netAmount = Math.max(0, amount - (share.discount || 0));
          return { ...share, amount, netAmount };
        } else {
          const originalShare =
            share.originalShare || share.share / (p.quantity || 1);
          const newShareValue = originalShare * qty;
          const amount = newShareValue;
          const netAmount = Math.max(0, amount - (share.discount || 0));
          return {
            ...share,
            share: newShareValue,
            originalShare,
            amount,
            netAmount,
          };
        }
      });
      const newProcedures = [...prev];
      newProcedures[index] = { ...p, quantity: qty, total, doctorShares };
      return newProcedures;
    });
  };

  const handleDescriptionChangeAt = (index: number, value: string) => {
    setProcedures(prev => {
      const newProcedures = [...prev];
      newProcedures[index] = { ...prev[index], description: value };
      return newProcedures;
    });
  };

  const handlePriceChangeAt = (index: number, value: string) => {
    setProcedures(prev => {
      const newPrice = Number.parseFloat(value) || 0;
      const p = prev[index];
      const newTotal = newPrice * p.quantity;

      // Recalculate doctor shares based on new price/total
      const doctorShares = p.doctorShares.map(share => {
        if (share.shareMode === "%") {
          const amount = (share.share / 100) * newTotal;
          const netAmount = Math.max(0, amount - (share.discount || 0));
          return { ...share, amount, netAmount };
        } else {
          // For fixed amount shares, keep the original share value but recalculate based on quantity
          const originalShare =
            share.originalShare || share.share / (p.quantity || 1);
          const newShareValue = originalShare * p.quantity;
          const amount = newShareValue;
          const netAmount = Math.max(0, amount - (share.discount || 0));
          return {
            ...share,
            share: newShareValue,
            originalShare,
            amount,
            netAmount,
          };
        }
      });

      const newProcedures = [...prev];
      newProcedures[index] = {
        ...p,
        price: newPrice,
        total: newTotal,
        doctorShares,
      };
      return newProcedures;
    });
  };

  const handleDiscountChangeAt = (index: number, value: string) => {
    setProcedures(prev => {
      const discount = Number.parseFloat(value) || 0;
      const p = prev[index];

      // Calculate the current hospital share (before discount)
      const totalDoctorShares = p.doctorShares.reduce(
        (acc, share) => acc + (share.amount || 0),
        0
      );
      const hospitalShare = Math.max(0, p.total - totalDoctorShares);

      // If discount would make net hospital share negative, return early without updating
      if (discount > hospitalShare) {
        toast.warning("Discount cannot be greater than hospital share");
        return prev;
      }

      const newProcedures = [...prev];
      newProcedures[index] = {
        ...prev[index],
        discount,
        discountMode: "value",
      };
      return newProcedures;
    });
  };

  const handleDoctorShareAmountChangeAt = (
    procIndex: number,
    shareIndex: number,
    value: string
  ) => {
    const newAmount = Number.parseFloat(value) || 0;
    setProcedures(prev => {
      const p = prev[procIndex];
      const shares = p.doctorShares.map((s, i) =>
        i === shareIndex
          ? {
            ...s,
            amount: newAmount,
            netAmount: Math.max(0, newAmount - (s.discount || 0)),
          }
          : s
      );
      const newProcedures = [...prev];
      newProcedures[procIndex] = { ...p, doctorShares: shares };
      return newProcedures;
    });
  };

  const handleDoctorShareDiscountChangeAt = (
    procIndex: number,
    shareIndex: number,
    value: string
  ) => {
    const newDiscount = Number.parseFloat(value) || 0;
    setProcedures(prev => {
      const p = prev[procIndex];
      const shares = p.doctorShares.map((s, i) =>
        i === shareIndex
          ? {
            ...s,
            discount: newDiscount,
            netAmount: Math.max(0, (s.amount || 0) - newDiscount),
          }
          : s
      );
      const newProcedures = [...prev];
      newProcedures[procIndex] = { ...p, doctorShares: shares };
      return newProcedures;
    });
  };

  const removeDoctorShare = (procIndex: number, shareIndex: number) => {
    setProcedures(prev => {
      const p = prev[procIndex];
      const filteredShares = p.doctorShares.filter((_, i) => i !== shareIndex);
      const newProcedures = [...prev];
      newProcedures[procIndex] = { ...p, doctorShares: filteredShares };
      return newProcedures;
    });
  };

  const handlePaymentChange = (value: string) => {
    const newPayment = Number.parseFloat(value) || 0;
    setPayment(newPayment);
  };

  const handleAdvanceChange = (value: string) => {
    const newAdvance = Number.parseFloat(value) || 0;
    setAdvance(newAdvance);
  };

  const computeSummary = () => {
    const subTotal = procedures.reduce((acc, p) => acc + p.total, 0);

    // Procedure-level discounts (now always in Rs)
    const procedureDiscount = procedures.reduce((acc, p) => {
      return acc + (p.discount || 0);
    }, 0);

    // Doctor share-level discounts
    const doctorShareDiscount = procedures.reduce((acc, p) => {
      const totalShareDiscount = p.doctorShares.reduce(
        (shareAcc, share) => shareAcc + (share.discount || 0),
        0
      );
      return acc + totalShareDiscount;
    }, 0);

    // Total discount = procedure + doctor share
    const totalDiscount = Math.round(procedureDiscount + doctorShareDiscount);

    const grandTotal = Math.round(subTotal - totalDiscount);
    const due = Math.max(0, grandTotal - payment - advance);

    return {
      subTotal,
      discountAmount: totalDiscount,
      grandTotal,
      payment,
      due,
      advance,
    };
  };

  const gatherData = async (print = false) => {
    if (!selectedPatient?._id) {
      toast.error("Please select a patient");
      return;
    }

    if (procedures.length === 0 || procedures.every(p => !p?.categoryId)) {
      toast.error("Please select at least one procedure");
      return;
    }

    // Optional: Add doctor selection validation if needed
    // if (!selectedDoctor?._id) {
    //   toast.error("Please select a doctor");
    //   return;
    // }

    const procedureItems = procedures
      .filter(p => p?.categoryId)
      .map(p => ({
        incomeCategoryId: p?.categoryId,
        description: p?.description,
        rate: p?.price,
        qty: p?.quantity,
        amount: p?.total,
        discount: p?.discount,
        discountMode: "value",
        shares: p.doctorShares.map(share => ({
          doctorId: share.doctorId,
          name: share.doctorName,
          share: share.share,
          shareMode: share.shareMode,
          amount: share.amount,
          discount: share.discount,
          netAmount: Math.max(0, share.netAmount),
        })),
      }));

    const { subTotal, discountAmount, grandTotal, payment, due, advance } =
      computeSummary();

    // Determine if this is an update or add operation
    const isUpdate = editData && editData._id;
    const apiEndpoint = isUpdate
      ? `${process.env.REACT_APP_ADMIN_API_URL}/income/updateProceduralIncome/${editData._id}`
      : `${process.env.REACT_APP_ADMIN_API_URL}/income/addProceduralIncome`;

    const data: any = {
      patientId: selectedPatient._id,
      procedureItems,
      subTotal,
      discount: discountAmount,
      discountMode: "value",
      grandTotal,
      payment,
      due,
      advance,
      payment_mode: paymentMode,
      file,
    };

    // Only add doctor information if a doctor is selected
    if (selectedDoctor?._id) {
      data.doctorId = selectedDoctor._id;
      data.doctorName = selectedDoctor.fullName;
    }

    // Add invoice note if provided
    if (invoiceNote) {
      data.invoiceNote = invoiceNote;
    }
    try {
      setLoading(true);
      const response = isUpdate
        ? await axios.patch(apiEndpoint, data, {
            headers: {
            "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          })
        : await axios.post(apiEndpoint, data, {
            headers: {
            "Content-Type": "multipart/form-data",
            // "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

      if (response.status === 200 || response.status === 201) {
        toast.success(
          isUpdate ? `${response.data?.message}` : `${response.data?.message}`
        );

        if (print) {
          setOpen(false);
          setTimeout(() => {
            setInvoiceData(response.data?.data || null);
            setOpen(true);
          }, 0);
        } else {
          navigate("/account/invoice");
        }
        if (!isUpdate) {
          setProcedures([
            {
              categoryId: "",
              categoryName: "",
              description: "",
              price: 0,
              quantity: 1,
              total: 0,
              discount: 0,
              discountMode: "value",
              doctorShares: [],
            },
          ]);
          setPayment(0);
          setAdvance(0);
          setSelectedDoctor(null);
          setInvoiceNote("");
        }
      } else {
        toast.error(isUpdate ? "Failed to update data" : "Failed to save data");
      }
    } catch (error) {
      console.error(
        isUpdate ? "Error updating data:" : "Error saving data:",
        error
      );
      toast.error(isUpdate ? "Error updating data" : "Error saving data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        background: "#ffffff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        overflow: "auto",
        width: "100%",
      }}
    >
      {/* Header Section */}
      <Grid container mb={2}>
        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            flexWrap="wrap"
            gap={2}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { md: "20px", xs: "16px" },
                fontWeight: 600,
                color: "#495057",
                mb: { xs: 1, md: 0 },
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Create Invoice
            </Typography>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "column", md: "row" }}
              gap={1}
              alignItems={{ xs: "stretch", md: "center" }}
              width={{ xs: "100%", md: "auto" }}
            >
              <Box display="flex" gap={1} alignItems="center" width={{ xs: "100%", md: "auto" }}>
                <Typography
                  sx={{ fontSize: { md: "14px", xs: "12px" }, fontWeight: 600, minWidth: 70 }}
                >
                  Search By:
                </Typography>
                <Select
                  className="custom-input"
                  value={searchType}
                  onChange={handleSearchTypeChange}
                  sx={{
                    borderRadius: "8px",
                    background: "#F5F5FB",
                    fontSize: "14px",
                    height: "32px",
                    minWidth: { xs: 100, md: 120 },
                    "& fieldset": { border: "none" },
                  }}
                  input={<OutlinedInput />}
                >
                  <MenuItem value="MRN">MRN</MenuItem>
                  <MenuItem value="Name">Name</MenuItem>
                  <MenuItem value="CNIC">CNIC</MenuItem>
                  <MenuItem value="Phone Number">Mobile #</MenuItem>
                </Select>
                <TextField
                  className="custom-input"
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  placeholder={`Enter ${searchType}`}
                  size="small"
                  type={
                    searchType === "Name" || searchType === "Phone Number"
                      ? "text"
                      : "number"
                  }
                  sx={{
                    "& .MuiInputBase-root": { height: "32px", fontSize: "14px" },
                    width: { xs: "100%", md: 180 },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{
                            minWidth: 32,
                            px: 1,
                            py: 0.5,
                            borderRadius: "8px",
                            boxShadow: "none",
                            fontSize: "14px",
                            height: "28px",
                            ml: 0.5,
                          }}
                          onClick={handleSearch}
                          tabIndex={-1}
                        >
                          <SearchIcon fontSize="small" />
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={handleKeyPress}
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleOpen}
                disabled={open}
                size="small"
                startIcon={<Add />}
                sx={{
                  minWidth: { xs: "100%", md: "100px" },
                  fontSize: { xs: "12px", md: "12px" },
                  fontWeight: "600",
                  textTransform: "none",
                  borderRadius: "8px",
                  background:
                    "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                  color: "#ffffff",
                  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                    boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": {
                    background: "#b0bec5",
                    color: "#ffffff",
                    opacity: 0.6,
                    cursor: "not-allowed",
                  },
                  "& .MuiButton-startIcon": {
                    marginRight: { xs: "4px", md: "8px" },
                  },
                  mt: { xs: 1, md: 0 },
                }}
              >
                Add Patient
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <PatientSearchDialog
        loading={loading}
        open={openSearchList}
        searchResults={searchResults.map(patient => ({
          ...patient,
          mrn: patient.mrn !== undefined ? String(patient.mrn) : "",
        }))}
        onClose={() => setOpenSearchList(false)}
      />
      {!selectedPatient?._id && (
        <Box mt={2}>
          <Typography
            sx={{ fontSize: 16, color: "#666", fontWeight: 600 }}
            variant="body2"
            color="textSecondary"
            textAlign="center"
          >
            Please select a patient to proceed
          </Typography>
        </Box>
      )}
      {/* Procedures Section */}
      {procedures.length > 0 && (
        <>
          <Box
            sx={{
              position: "relative",
              mt: 2,
              borderRadius: "8px",
              border: "1px solid #e9ecef",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: 16,
                fontWeight: 700,
                px: 2,
                py: 0,
                color: "#495057",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f1f3f5",
                borderRadius: "8px 8px 0 0",
              }}
            >
              Procedures
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: "#666" }}
                >
                  MRN: {patient?.mrn || editData?.patientInfo?.mrn || "N/A"}
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: "#666" }}
                >
                  Patient:{" "}
                  {patient?.patientName ||
                    editData?.patientInfo?.patientName ||
                    "N/A"}
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: "#666" }}
                >
                  Guardian Name:{" "}
                  {patient?.guardiansName ||
                    editData?.patientInfo?.guardiansName ||
                    "N/A"}
                </Typography>
                <Tooltip title="Add Procedure" arrow>
                  <AddIcon
                    onClick={addProcedureSection}
                    sx={{
                      fontSize: "20px",
                      color: "#fff",
                      bgcolor: "green",
                      borderRadius: "100%",
                      p: 0.5,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              </Box>
            </Typography>

            {procedures.map((p, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1,
                  mx: 2,
                  border: "1px solid #e9ecef",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <TableContainer
                  sx={{
                    overflowY: "hidden",
                    overflowX: "auto",
                    "&::-webkit-scrollbar": {
                      height: "3px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888",
                      borderRadius: "3px",
                      display: "none",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#555",
                    },
                  }}
                >
                  <Table sx={{ minWidth: { xs: "900px", md: "100%" } }}>
                    <TableHead sx={{ overflow: "hidden" }}>
                      <TableRow
                        sx={{
                          backgroundColor: "#1976d2",
                          color: "#fff",
                          overflow: "hidden",
                        }}
                      >
                        <TableCell
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#fff",
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                          }}
                        >
                          Select Procedure
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#fff",
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                          }}
                        >
                          Description
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#fff",
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                          }}
                        >
                          Price
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#fff",
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                          }}
                        >
                          Quantity
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#fff",
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                          }}
                        >
                          Total
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#fff",
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                          }}
                        >
                          Hospital Share
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#fff",
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                          }}
                        >
                          Discount Amount
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#fff",
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          Net Hospital Share
                          <Cancel
                            onClick={() => removeProcedureSection(index)}
                            sx={{ fontSize: "14px", color: "red" }}
                            cursor="pointer"
                          />
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ backgroundColor: "#fff" }}>
                        <TableCell
                          sx={{
                            p: 0,
                            borderRight: "1px solid #e9ecef",
                            width: 200,
                          }}
                        >
                          <Autocomplete
                            size="small"
                            options={incomeData}
                            getOptionLabel={option => option.categoryName}
                            value={
                              incomeData.find(
                                item => item._id === p?.categoryId
                              ) || null
                            }
                            onChange={(event, value) =>
                              handleProcedureChange(index, event, value)
                            }
                            renderInput={params => (
                              <TextField
                                className="custom-input2"
                                {...params}
                                size="small"
                                placeholder="Select procedure"
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                    fontSize: "12px",
                                  },
                                }}
                              />
                            )}
                          />
                        </TableCell>
                        {p?.categoryId && (
                          <>
                            <TableCell
                              sx={{
                                p: 0.2,
                                borderRight: "1px solid #e9ecef",
                                width: 200,
                              }}
                            >
                              <TextField
                                className="custom-input2"
                                fullWidth
                                size="small"
                                value={p.description}
                                onChange={e =>
                                  handleDescriptionChangeAt(
                                    index,
                                    e.target.value
                                  )
                                }
                                placeholder="Description"
                                sx={{
                                  "& .MuiInputBase-root": {
                                    minHeight: "20px",
                                    height: "20px",
                                    fontSize: "10px",
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "0px",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{ p: 0.2, borderRight: "1px solid #e9ecef" }}
                            >
                              <TextField
                                className="custom-input2"
                                fullWidth
                                size="small"
                                type="number"
                                placeholder="0"
                                value={p.price === 0 ? "" : p.price}
                                onChange={e =>
                                  handlePriceChangeAt(index, e.target.value)
                                }
                                inputProps={{ min: 0, step: "0.01" }}
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                    fontSize: "12px",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{ p: 0.2, borderRight: "1px solid #e9ecef" }}
                            >
                              <TextField
                                className="custom-input2"
                                fullWidth
                                size="small"
                                type="number"
                                value={p.quantity}
                                onChange={e =>
                                  handleQuantityChangeAt(index, e.target.value)
                                }
                                inputProps={{ min: 1 }}
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                    fontSize: "12px",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{ p: 0.2, borderRight: "1px solid #e9ecef" }}
                            >
                              <TextField
                                className="custom-input2"
                                fullWidth
                                disabled
                                size="small"
                                type="number"
                                value={p.total}
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                    fontSize: "12px",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{ p: 0.2, borderRight: "1px solid #e9ecef" }}
                            >
                              <TextField
                                className="custom-input2"
                                fullWidth
                                disabled
                                placeholder="0"
                                size="small"
                                type="number"
                                value={(() => {
                                  const totalDoctorShares =
                                    p.doctorShares.reduce(
                                      (acc, share) => acc + (share.amount || 0),
                                      0
                                    );
                                  const hospitalShare = Math.max(
                                    0,
                                    p.total - totalDoctorShares
                                  );
                                  return hospitalShare === 0
                                    ? ""
                                    : Math.round(hospitalShare);
                                })()}
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                    fontSize: "12px",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{ p: 0.2, borderRight: "1px solid #e9ecef" }}
                            >
                              {(() => {
                                const totalDoctorShares = p.doctorShares.reduce(
                                  (acc, share) => acc + (share.amount || 0),
                                  0
                                );
                                const hospitalShare = Math.max(
                                  0,
                                  p.total - totalDoctorShares
                                );
                                const netHospitalShare = Math.max(
                                  0,
                                  hospitalShare - (p.discount || 0)
                                );
                                const isNetHospitalShareZero =
                                  netHospitalShare === 0;

                                return (
                                  <Tooltip
                                    title={
                                      isNetHospitalShareZero
                                        ? "Net hospital share is zero - no more discount can be applied"
                                        : ""
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <TextField
                                      className="custom-input2"
                                      fullWidth
                                      size="small"
                                      type="number"
                                      placeholder="0"
                                      value={
                                        Math.round(p.discount) === 0
                                          ? ""
                                          : p.discount
                                      }
                                      onChange={e => {
                                        if (
                                          Number(e.target.value) > hospitalShare
                                        ) {
                                          return;
                                        }
                                        handleDiscountChangeAt(
                                          index,
                                          e.target.value
                                        );
                                      }}
                                      inputProps={{ min: 0 }}
                                      sx={{
                                        "& .MuiInputBase-root": {
                                          height: "32px",
                                          fontSize: "12px",
                                        },
                                        ...(isNetHospitalShareZero && {
                                          "& .MuiInputBase-root": {
                                            height: "32px",
                                            fontSize: "12px",
                                            backgroundColor: "#ffe6e6",
                                            border: "1px solid #ff9999",
                                            cursor: "not-allowed",
                                          },
                                        }),
                                      }}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            Rs
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                            <TableCell
                              sx={{ p: 0.2, borderRight: "1px solid #e9ecef" }}
                            >
                              <TextField
                                className="custom-input2"
                                fullWidth
                                disabled
                                placeholder="0"
                                size="small"
                                type="number"
                                value={(() => {
                                  const totalDoctorShares =
                                    p.doctorShares.reduce(
                                      (acc, share) => acc + (share.amount || 0),
                                      0
                                    );
                                  const hospitalShare = Math.max(
                                    0,
                                    p.total - totalDoctorShares
                                  );
                                  const netHospitalShare = Math.max(
                                    0,
                                    hospitalShare - (p.discount || 0)
                                  );
                                  return netHospitalShare === 0
                                    ? ""
                                    : Math.round(netHospitalShare);
                                })()}
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                    fontSize: "12px",
                                  },
                                }}
                              />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Doctor Shares Table */}
                {p?.categoryId && (
                  <Box>
                    <TableContainer
                      sx={{
                        overflowX: "auto",
                        "&::-webkit-scrollbar": {
                          height: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f1f1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#888",
                          borderRadius: "3px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          backgroundColor: "#555",
                        },
                      }}
                    >
                      <Table sx={{ minWidth: { xs: "600px", md: "100%" } }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "lightgray" }}>
                            <TableCell
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#000",
                                px: 1,
                                py: 0,
                                borderRight: "1px solid #e9ecef",
                              }}
                            >
                              Doctor Name
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#000",
                                px: 1,
                                py: 0,
                                borderRight: "1px solid #e9ecef",
                              }}
                            >
                              Share
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#000",
                                px: 1,
                                py: 0,
                                borderRight: "1px solid #e9ecef",
                              }}
                            >
                              Amount
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#000",
                                px: 1,
                                py: 0,
                                borderRight: "1px solid #e9ecef",
                              }}
                            >
                              Discount
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#000",
                                px: 1,
                                py: 0,
                                borderRight: "1px solid #e9ecef",
                              }}
                            >
                              Net Amount
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#000",
                                px: 1,
                                py: 0,
                                borderRight: "1px solid #e9ecef",
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {p.doctorShares.map((share, sIndex) => (
                            <TableRow
                              key={`${p.categoryId}-${sIndex}`}
                              sx={{ backgroundColor: "#fff" }}
                            >
                              <TableCell
                                sx={{
                                  p: 0.2,
                                  borderRight: "1px solid #e9ecef",
                                }}
                              >
                                <TextField
                                  className="custom-input2"
                                  disabled
                                  size="small"
                                  value={share.doctorName}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "32px",
                                      fontSize: "12px",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  p: 0.2,
                                  borderRight: "1px solid #e9ecef",
                                }}
                              >
                                <TextField
                                  className="custom-input2"
                                  disabled
                                  size="small"
                                  type="number"
                                  value={share.share}
                                  InputProps={
                                    share.shareMode === "%"
                                      ? {
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            %
                                          </InputAdornment>
                                        ),
                                      }
                                      : {
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            Rs
                                          </InputAdornment>
                                        ),
                                      }
                                  }
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "32px",
                                      fontSize: "12px",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  p: 0.2,
                                  borderRight: "1px solid #e9ecef",
                                }}
                              >
                                <TextField
                                  className="custom-input2"
                                  size="small"
                                  type="number"
                                  value={
                                    Math.round(share.amount) === 0
                                      ? ""
                                      : Math.round(share.amount)
                                  }
                                  onChange={e =>
                                    handleDoctorShareAmountChangeAt(
                                      index,
                                      sIndex,
                                      e.target.value
                                    )
                                  }
                                  inputProps={{ min: 0 }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "32px",
                                      fontSize: "12px",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  p: 0.2,
                                  borderRight: "1px solid #e9ecef",
                                }}
                              >
                                <TextField
                                  className="custom-input2"
                                  size="small"
                                  placeholder="0"
                                  type="number"
                                  value={
                                    Math.round(share.discount) === 0
                                      ? ""
                                      : Math.round(share.discount)
                                  }
                                  onChange={e => {
                                    if (Number(e.target.value) > share.amount) {
                                      return;
                                    }
                                    handleDoctorShareDiscountChangeAt(
                                      index,
                                      sIndex,
                                      e.target.value
                                    );
                                  }}
                                  inputProps={{ min: 0 }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "32px",
                                      fontSize: "12px",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  p: 0.2,
                                  borderRight: "1px solid #e9ecef",
                                }}
                              >
                                <TextField
                                  className="custom-input2"
                                  disabled
                                  size="small"
                                  type="number"
                                  value={Math.round(
                                    Math.max(0, share.netAmount)
                                  )}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "32px",
                                      fontSize: "12px",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  p: 0.2,
                                  borderRight: "1px solid #e9ecef",
                                  textAlign: "center",
                                }}
                              >
                                <Cancel
                                  onClick={() =>
                                    removeDoctorShare(index, sIndex)
                                  }
                                  sx={{
                                    fontSize: "16px",
                                    color: "red",
                                    cursor: "pointer",
                                    "&:hover": {
                                      color: "#d32f2f",
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            ))}

            {/* Summary Section */}
            {procedures.some(p => p?.categoryId) && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #f9fafc, #f3f6ff)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                }}
              >
                {(() => {
                  const {
                    subTotal,
                    discountAmount,
                    grandTotal,
                    payment,
                    due,
                    advance,
                  } = computeSummary();
                  return (
                    <Box
                      display="grid"
                      gap={2}
                      sx={{
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(6, 1fr)",
                        },
                        textAlign: "center",
                      }}
                    >
                      <TextField
                        className="custom-input2"
                        label="Sub Total"
                        value={Math.round(subTotal)}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{
                          background: "#fff",
                          borderRadius: "8px",
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "12px",
                          },
                        }}
                      />
                      <TextField
                        className="custom-input2"
                        label="Discount"
                        value={Math.round(discountAmount)}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{
                          background: "#fff",
                          borderRadius: "8px",
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "12px",
                          },
                        }}
                      />
                      <TextField
                        className="custom-input2"
                        label="Grand Total"
                        value={Math.round(grandTotal)}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{
                          background: "#fff",
                          borderRadius: "8px",
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "12px",
                          },
                        }}
                      />
                      <TextField
                        className="custom-input2"
                        label="Payment"
                        placeholder="0"
                        // disabled={editData && editData._id ? true : false}
                        value={payment === 0 ? "" : payment}
                        onChange={e => handlePaymentChange(e.target.value)}
                        type="number"
                        inputProps={{ min: 0 }}
                        size="small"
                        sx={{
                          background: "#fff",
                          borderRadius: "8px",
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "12px",
                          },
                        }}
                      />
                      <TextField
                        className="custom-input2"
                        label="Due"
                        value={Math.round(due)}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{
                          background: "#fff",
                          borderRadius: "8px",
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "12px",
                          },
                        }}
                      />
                      <TextField
                        className="custom-input2"
                        label="Advance"
                        placeholder="0"
                        value={advance === 0 ? "" : advance}
                        onChange={e => handleAdvanceChange(e.target.value)}
                        type="number"
                        inputProps={{ min: 0, readOnly: true }}
                        size="small"
                        sx={{
                          background: "#fff",
                          borderRadius: "8px",
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "12px",
                          },
                        }}
                      />
                    </Box>
                  );
                })()}
              </Box>
            )}

            <Box
              mt={2}
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="flex-end"
              alignItems={{ xs: "stretch", md: "center" }}
              gap={2}
            >
              <Box sx={{ minWidth: { xs: "100%", md: 180 }, display: "flex","mb":1, gap: 1, alignItems: "center" }}>
                <Box sx={{ flex: 1 }}>
                  <input
                    accept="*/*"
                    style={{ display: "none" }}
                    id="raised-button-file"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="raised-button-file">
                    <TextField
                      className="custom-input"
                      fullWidth
                      size="small"
                      value={file ? file.name : existingAttachmentUrl ? existingAttachmentUrl.split('/').pop() || existingAttachmentUrl : "Upload File"}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end" sx={{ cursor: "pointer",mr:2 }}>
                            <IconButton component="span" edge="end">
                              <CloudUpload color="primary" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        cursor: "pointer",
                        "& .MuiInputBase-root": {
                          cursor: "pointer",
                          height: "36px",
                          fontSize: "14px",
                          borderRadius: "8px",
                        },
                        "& input": {
                          cursor: "pointer",
                        },
                      }}
                    />
                  </label>
                </Box>
                {existingAttachmentUrl && !file && (
                  <IconButton
                    color="primary"
                    onClick={() => setAttachmentDialogOpen(true)}
                    sx={{
                      height: "32px",
                      width: "32px",
                      borderRadius: "8px",
                      mb:0.5,
                      border: "1px solid #1976d2",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <TextField
                size="small"
                label="Invoice Note (Optional)"
                placeholder="Add a note for this invoice"
                value={invoiceNote}
                onChange={(e) => setInvoiceNote(e.target.value)}
                multiline
                rows={1}
                sx={{
                  mb:1,
                  minWidth: { xs: "100%", md: 200 },
                  "& .MuiInputBase-root": {
                    fontSize: "14px",
                    borderRadius: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "12px",
                  },
                }}
                className="custom-input"
              />
              <Box sx={{ minWidth: { xs: "100%", md: 200 } }}>
                <Autocomplete
                  size="small"
                  options={doctors}
                  getOptionLabel={option => option.fullName}
                  value={selectedDoctor}
                  onChange={(event, value) => setSelectedDoctor(value)}
                  renderInput={params => (
                    <TextField
                      className="custom-input"
                      {...params}
                      label="Select Doctor (Optional)"
                      placeholder="Choose a doctor"
                      size="small"
                      sx={{
                        mb: { xs: 0, md: 1 },
                        "& .MuiInputBase-root": {
                          height: "36px",
                          fontSize: "14px",
                          borderRadius: "8px",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "12px",
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <FormControl sx={{ minWidth: { xs: "100%", md: 140 } }}>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  className="custom-input"
                  value={paymentMode}
                  label="Payment Mode"
                  size="small"
                  onChange={e => setPaymentMode(e.target.value)}
                  sx={{
                    borderRadius: "8px",
                    background: "#F5F5FB",
                    fontSize: "14px",
                    height: "30px",
                    mb: { xs: 0, md: 1 },
                    "& fieldset": { border: "none" },
                  }}
                  input={
                    <OutlinedInput
                      className="custom-input"
                      label="Payment Mode"
                    />
                  }
                >
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                  <MenuItem value="Refund">Refund</MenuItem>
                </Select>
              </FormControl>
              <Box
                display="flex"
                gap={1}
                flexDirection={{ xs: "column", sm: "row" }}
                width={{ xs: "100%", md: "auto" }}
              >
                <Button
                  variant="contained"
                  onClick={() => gatherData(true)}
                  disabled={loading || !procedures.some(p => p?.categoryId)}
                  startIcon={
                    loading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Save size={14} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    py: 0.5,
                    px: 1,
                    mb: { xs: 0, md: 1 },
                    mr: { xs: 0, md: 1 },
                    width: { xs: "100%", sm: "auto" },
                    background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                    color: "#fff",
                    cursor: loading ? "not-allowed" : "pointer",
                    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(90deg, #1565c0, #1e88e5)",
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#ccc",
                      boxShadow: "none",
                      color: "gray",
                    },
                  }}
                >
                  {loading
                    ? editData && editData._id
                      ? "Updating..."
                      : "Saving..."
                    : editData && editData._id
                      ? "Update & print"
                      : "Save & print"}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => gatherData(false)}
                  disabled={loading || !procedures.some(p => p?.categoryId)}
                  startIcon={
                    loading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Save size={14} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    py: 0.5,
                    px: 1,
                    mb: { xs: 0, md: 1 },
                    mr: { xs: 0, md: 1 },
                    width: { xs: "100%", sm: "auto" },
                    background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                    color: "#fff",
                    cursor: loading ? "not-allowed" : "pointer",
                    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(90deg, #1565c0, #1e88e5)",
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#ccc",
                      boxShadow: "none",
                      color: "gray",
                    },
                  }}
                >
                  {loading
                    ? editData && editData._id
                      ? "Updating..."
                      : "Saving..."
                    : editData && editData._id
                      ? "Update "
                      : "Save "}
                </Button>
              </Box>
            </Box>
          </Box>
          <InvoiceThermalReceipt
            open={open}
            invoice={invoiceData}
            hospitalName={projectProfile?.hospitalName || "Hospital Name"}
            hospitalPhone={projectProfile?.phoneNo || "Hospital Phone"}
          />
        </>
      )}
      <Dialog open={patientReg} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <AccountPatientRegistration closeAllScreen={handleClose} />
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, index: -1, value: null })
        }
      >
        <DialogTitle>Confirm Duplicate Procedure</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to add the same procedure again?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, index: -1, value: null })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              confirmProcedureChange(confirmDialog.index, confirmDialog.value);
              setConfirmDialog({ open: false, index: -1, value: null });
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <AttachmentViewer
        open={attachmentDialogOpen}
        onClose={() => setAttachmentDialogOpen(false)}
        attachmentUrl={existingAttachmentUrl}
        title="Invoice Attachment"
      />
    </Box>
  );
};

export default IncomeManagement;
